import React, { memo, ReactNode } from 'react';

import Tooltip from '@plasma/ui.display.tooltip';
import PopConfirm from '@plasma/ui.feedback.pop-confirm';
import Button from '@plasma/ui.general.button';
import Select from '@plasma/ui.input.select';
import Modal from '@plasma/ui.layout.modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import TopBarThemed from '../../components/top-bar-themed';
import usePlantLayoutAreaDrawingsHook from './plant-layout-area-drawings.hook';
import * as styled from './plant-layout-area-drawings.styles';
import { NormalPlasmaButton, NormalPlasmaSelect } from './plant-layout-area-drawings.styles';
import themes from './themes';

const { Option } = Select;

export interface PlantLayoutAreaDrawingsProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = PlantLayoutAreaDrawingsProps & ThemeOverride;

function PlantLayoutAreaDrawings(props: Props) {
  const { children, className, style, size, themeMode } = props;
  const superUserRole = sessionStorage.getItem('superUserRole');

  const {
    t,
    showPhoto,
    loadingFile,
    buttons,
    showModal,
    areaNameInModal,
    distinctArea,
    handleConfirmSelection,
    showButtonBox,
    iFrameSrc,
    guidByProd,
    guidByProdArray,
    pageNumber,
    roleID,
    buttonTitle,
    handleGoHome,
    handleButtonClick,
    handleShowModal,
    handleSaveFromModal,
    HandleModalCancel,
    handleAreaChangeInModal,
    handleFileUpload,
    handleButtonBoxOpen,
    handleNextPage,
    zoneAndAreaCodesFromPlantLayoutApi,
    handleZoneChangeInModal,
    handlePreviousPage,
    showAreaZoneDrawingSelectionModal,
    setshowAreaZoneDrawingSelectionModal,
    setButtonTitle,
    zoneNameInModal,
    selectedZoneDrawing,
    setSelectedZoneDrawing,
    handleDeletePDF,
    setshowPhoto,
    setpageNumber,
  } = usePlantLayoutAreaDrawingsHook(props);

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.PlantLayoutAreaDrawings className={`plant-layout-area-drawings ${className ?? ''}`.trim()} style={style}>
        <TopBarThemed topHeading={t('plant-layout-and-area-drawing')} />
        {loadingFile && <LoadingComponent />}

        <Modal
          title={t('upload-files')}
          width={'fit-content'}
          okText={t('save')}
          cancelText={t('cancel')}
          visible={showModal}
          onCancel={() => HandleModalCancel()}
          onOk={() => handleSaveFromModal()}
          destroyOnClose
        >
          <div style={{ display: 'flex', flexDirection: 'column', color: 'red' }}>
            <span>
              {' '}
              <strong>Zone Drawings :</strong> Select Area → Select Zone → Select PDF → Click Save
            </span>
            <span>
              {' '}
              <strong>Area Drawings :</strong> Select Area → Select PDF → Click Save{' '}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              background: 'linear-gradient(to top left, #f0f0f5, #33adff55)',
              padding: '15px',
              borderRadius: '5px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4vh', marginTop: '3vh' }}>
              <div
                style={{
                  height: '3vh',
                  width: '10vw',
                  fontWeight: 'bold',
                  marginRight: '3vw',
                  fontSize: '15px',
                  border: '1px solid #75a3a3',
                  backgroundColor: '#f0f5f5',
                  padding: '5px 10px',
                  borderRadius: '5px',
                }}
              >
                {t('area')}
              </div>

              <NormalPlasmaSelect
                style={{ minWidth: '8vw', border: '1px solid #75a3a3', borderRadius: '3px' }}
                dropdownStyle={{ minWidth: '10vw' }}
                placeholder="Select Area"
                onChange={handleAreaChangeInModal}
                value={areaNameInModal}
                allowClear
              >
                {distinctArea?.map((item, index) => (
                  <Option key={index} value={item}>
                    {' '}
                    {item}{' '}
                  </Option>
                ))}
              </NormalPlasmaSelect>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4vh', marginTop: '3vh' }}>
              <div
                style={{
                  height: '3vh',
                  width: '10vw',
                  fontWeight: 'bold',
                  marginRight: '3vw',
                  fontSize: '15px',
                  border: '1px solid #75a3a3',
                  backgroundColor: '#f0f5f5',
                  padding: '5px 10px',
                  borderRadius: '5px',
                }}
              >
                {t('zone')}
              </div>

              <NormalPlasmaSelect
                style={{ minWidth: '8vw', border: '1px solid #75a3a3', borderRadius: '3px' }}
                dropdownStyle={{ minWidth: '10vw' }}
                placeholder="Select Zone"
                onChange={handleZoneChangeInModal}
                value={zoneNameInModal}
                disabled={!areaNameInModal}
                allowClear
              >
                {Array.from(
                  new Set(
                    zoneAndAreaCodesFromPlantLayoutApi
                      ?.filter((z) => {
                        if (!z.areacode || !areaNameInModal) return false;

                        const isMatchingArea = z.areacode.toLowerCase() === areaNameInModal.toLowerCase();
                        //ensure zonecode exists before filtering exclusions
                        const zone = z.zonecode?.toLowerCase() || '';
                        const isNotExcluded =
                          zone.toLowerCase() !== 'verticlelooper' &&
                          zone.toLowerCase() !== 'vertical looper' &&
                          zone.toLowerCase() !== 'separators';
                        return isMatchingArea && isNotExcluded;
                      })
                      .map((item) => item.zonecode),
                  ),
                ).map((zoneCode, index) => (
                  <Option key={index} value={zoneCode}>
                    {' '}
                    {zoneCode}{' '}
                  </Option>
                ))}
              </NormalPlasmaSelect>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2vh' }}>
              <div
                style={{
                  height: '3vh',
                  width: '6vw',
                  fontWeight: 'bold',
                  marginRight: '3vw',
                  fontSize: '15px',
                  border: '1px solid #75a3a3',
                  backgroundColor: '#f0f5f5',
                  padding: '5px 10px',
                  borderRadius: '5px',
                }}
              >
                {t('select-pdf')}
              </div>

              <input type="file" accept="application/pdf" onChange={handleFileUpload} />
            </div>
          </div>
        </Modal>

        <div style={{ display: 'flex', marginTop: '1.5vh', justifyContent: 'flex-end', overflow: 'hidden' }}>
          {showPhoto && (
            <div
              style={{
                border: '2px solid #b0ccda',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                marginTop: '0.5vh',
                marginLeft: '0.2vh',
                flex: '1',
                backgroundColor: '#cccccc99',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {guidByProd && (
                  <div style={{ display: 'flex' }}>
                    <PopConfirm
                      //title = 'Are you sure you want to delete this family ?'
                      title={<div>{t('delete-confirmation-text')} ?</div>}
                      onConfirm={handleDeletePDF}
                      okText={t('delete')}
                      cancelText={t('cancel')}
                    >
                      <Button
                        style={{ width: '2vw', borderRadius: '2px' }}
                        prefixIcon={{ name: 'delete' }}
                        disabled={!superUserRole}
                      />
                    </PopConfirm>
                  </div>
                )}
              </div>

              <iframe
                onMouseDown={() => console.log('iframeClicked')}
                src={iFrameSrc}
                title="PDF"
                style={{ height: '100%', width: '100%' }}
              />

              <h3
                style={{
                  borderRadius: '2px',
                  fontSize: '1.2vh',
                  marginLeft: '8px',
                  backgroundColor: '#f2eded99',
                  fontWeight: 'bold',
                  color: '#0066ccf0',
                }}
              >
                {selectedZoneDrawing}
              </h3>
            </div>
          )}

          <styled.ButtonContainer style={{ marginBottom: 0 }} showButtonBox={showButtonBox}>
            {/* <styled.ArrowButton
              showButtonBox={showButtonBox}
              onClick={handleButtonBoxOpen}
              prefixIcon={{name: showButtonBox ? 'arrow_forward_ios' : 'arrow_back_ios'}}
            /> */}

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px', alignItems: 'center' }}>
              <div style={{ fontSize: '17px', fontWeight: 'bold' }}>{t('areas')}</div>

              {superUserRole && (
                <NormalPlasmaButton style={{ borderRadius: '4px' }} title={t('upload-files')} onClick={handleShowModal}>
                  {t('upload-files')}
                </NormalPlasmaButton>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                marginTop: '4px',
                flexDirection: 'column',
                alignItems: 'center',
                overflowY: 'auto',
                borderTop: '2px solid black',
              }}
            >
              {buttons?.map((button, index) => (
                <styled.ThemedButton
                  key={index}
                  style={{ borderRadius: '4px' }}
                  isActive={button.title === buttonTitle}
                  title={button.title}
                  onClick={() => handleButtonClick(button.title)}
                />
              ))}
            </div>
          </styled.ButtonContainer>
        </div>

        <Modal
          title={`${t('area-zone-drawing')} - ${buttonTitle.replace(/[^a-zA-Z\s]/g, '').trim()}`}
          width={'22vw'}
          okText={t('ok')}
          cancelText={t('cancel')}
          visible={showAreaZoneDrawingSelectionModal}
          onCancel={() => HandleModalCancel()}
          onOk={() => {
            handleConfirmSelection();
          }}
          destroyOnClose
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              background: 'linear-gradient(to top left, #f0f0f5, #33adff55)',
              padding: '15px',
              borderRadius: '5px',
              height: '9vh',
              justifyContent: 'center',
            }}
          >
            <h4>{t('select-area-or-zone')}</h4>
            <NormalPlasmaSelect
              placeholder={t('select')}
              value={selectedZoneDrawing}
              allowClear
              onChange={(value: string) => setSelectedZoneDrawing(value)}
            >
              <option value={buttonTitle}> {buttonTitle} </option>
              {Array.from(
                new Set(
                  zoneAndAreaCodesFromPlantLayoutApi
                    ?.filter((z) => {
                      const isMatchingArea = z.areacode.toLowerCase() === buttonTitle.toLowerCase();

                      // Define strings to exclude (Vertical Looper, separators, etc.)
                      const isNotExcluded =
                        z.zonecode.toLowerCase() !== 'verticlelooper' &&
                        z.zonecode.toLowerCase() !== 'vertical looper' &&
                        z.zonecode.toLowerCase() !== 'separators';
                      return isMatchingArea && isNotExcluded;
                    })
                    .map((item) => item.zonecode),
                ),
              ).map((zoneCode, index) => (
                <Option key={index} value={zoneCode}>
                  {' '}
                  {zoneCode}{' '}
                </Option>
              ))}
            </NormalPlasmaSelect>
          </div>
        </Modal>
      </styled.PlantLayoutAreaDrawings>
    </ThemeProvider>
  );
}

export default memo(PlantLayoutAreaDrawings) as unknown as typeof PlantLayoutAreaDrawings;
